import React, {
  createContext, useContext, useRef, useCallback, useMemo, useEffect,
} from 'react';
import { createActor, Actor } from 'xstate';
import { useSelector } from '@xstate/react';
import type { UppyFile } from '@uppy/core';
import {
  universalMediaUploaderMachine,
  type UniversalMediaUploaderContext as MachineContext,
} from '../machines/universal-media-uploader-machine';
import type {
  InitializeConfig,
  UniversalMediaUploaderEvent,
  AttachmentData,
  UppyMeta,
  UppyBody,
} from '../machines/universal-media-uploader-types';
import { EVENT_TYPES } from '../machines/universal-media-uploader-types';
import formatRestrictionError from '../../utils/format-restriction-error';
import resizeImageIfNeeded from '../../utils/resize-image';

type UploaderActorRef = Actor<typeof universalMediaUploaderMachine>;

interface UniversalMediaUploaderContextValue {
  actors: Map<string, UploaderActorRef>;
  getOrCreateActor: (uploaderId: string) => UploaderActorRef;
  removeActor: (uploaderId: string) => void;
  isAnyUploading: () => boolean;
}

const UniversalMediaUploaderContext = createContext<UniversalMediaUploaderContextValue | null>(null);

interface UniversalMediaUploaderProviderProps {
  children: React.ReactNode;
  onUploadingChange?: (isUploading: boolean) => void;
}

export const UniversalMediaUploaderProvider: React.FC<UniversalMediaUploaderProviderProps> = ({ children, onUploadingChange }) => {
  const actorsRef = useRef<Map<string, UploaderActorRef>>(new Map());
  const actorSubscriptionsRef = useRef<Map<string, { unsubscribe: () => void }>>(new Map());
  const lastUploadingRef = useRef(false);
  const onUploadingChangeRef = useRef(onUploadingChange);
  onUploadingChangeRef.current = onUploadingChange;

  const isAnyUploading = useCallback((): boolean => {
    for (const actor of actorsRef.current.values()) {
      const snapshot = actor.getSnapshot();
      if (snapshot.matches('validating') || snapshot.matches('uploading')) {
        return true;
      }
    }
    return false;
  }, []);

  const notifyUploadingChange = useCallback(() => {
    const uploading = isAnyUploading();
    if (uploading !== lastUploadingRef.current) {
      lastUploadingRef.current = uploading;
      onUploadingChangeRef.current?.(uploading);
    }
  }, [isAnyUploading]);

  const getOrCreateActor = useCallback((uploaderId: string): UploaderActorRef => {
    const trimmedId = uploaderId.trim();
    if (!trimmedId) {
      throw new Error('uploaderId must be a non-empty string');
    }

    let actor = actorsRef.current.get(trimmedId);

    if (!actor) {
      actor = createActor(universalMediaUploaderMachine, {
        input: { uploaderId: trimmedId },
      });
      actor.start();
      actorsRef.current.set(trimmedId, actor);

      const subscription = actor.subscribe(() => {
        notifyUploadingChange();
      });
      actorSubscriptionsRef.current.set(trimmedId, subscription);
    }

    return actor;
  }, [notifyUploadingChange]);

  const removeActor = useCallback((uploaderId: string) => {
    const trimmedId = uploaderId.trim();
    if (!trimmedId) return;

    const subscription = actorSubscriptionsRef.current.get(trimmedId);
    if (subscription) {
      subscription.unsubscribe();
      actorSubscriptionsRef.current.delete(trimmedId);
    }

    const actor = actorsRef.current.get(trimmedId);
    if (actor) {
      actor.stop();
      actorsRef.current.delete(trimmedId);
    }

    notifyUploadingChange();
  }, [notifyUploadingChange]);

  const contextValue = useMemo(
    () => ({
      actors: actorsRef.current,
      getOrCreateActor,
      removeActor,
      isAnyUploading,
    }),
    [getOrCreateActor, removeActor, isAnyUploading],
  );

  useEffect(() => () => {
    actorSubscriptionsRef.current.forEach((sub) => sub.unsubscribe());
    actorSubscriptionsRef.current.clear();
    actorsRef.current.forEach((actor) => {
      actor.stop();
    });
    actorsRef.current.clear();
  }, []);

  return (
    <UniversalMediaUploaderContext.Provider value={contextValue}>
      {children}
    </UniversalMediaUploaderContext.Provider>
  );
};

export const useUniversalMediaUploaderContext = () => {
  const context = useContext(UniversalMediaUploaderContext);
  if (!context) {
    throw new Error('useUniversalMediaUploaderContext must be used within UniversalMediaUploaderProvider');
  }
  return context;
};

export interface UseUniversalMediaUploaderReturn {
  // State
  state: string;
  isIdle: boolean;
  isUploading: boolean;
  isCompleted: boolean;
  isError: boolean;
  isUninitialized: boolean;
  // Context values
  currentFile: UppyFile<UppyMeta, UppyBody> | null; // Computed from files map for backward compatibility
  uploadProgress: number;
  error: Error | null;
  attachmentData: AttachmentData | null;
  uppyInstance: MachineContext['uppyInstance'];
  wasRecentlyUploaded: boolean;
  multiFileMode: boolean;
  files: MachineContext['files'];
  failedFiles: MachineContext['failedFiles'];
  // Actions
  initialize: (config: InitializeConfig) => void;
  sendEvent: (event: UniversalMediaUploaderEvent) => void;
  cancelUpload: () => void;
  removeFile: () => void;
  clearError: () => void;
  reset: () => void;
  /**
   * Suppresses the next FILE_REMOVED machine event for the given Uppy file ID.
   * Call this before uppy.removeFile() to auto-remove from Uppy's queue without
   * clearing the machine's files entry (which keeps the status card visible).
   */
  suppressFileRemoved: (fileId: string) => void;
  /**
   * Cancels a pending suppressFileRemoved for the given file ID.
   * Call this in the catch block if uppy.removeFile() throws, so the suppress
   * entry doesn't silently swallow a future file-removed event.
   */
  unsuppressFileRemoved: (fileId: string) => void;
}

export const useUniversalMediaUploader = (
  uploaderId: string,
  config?: InitializeConfig,
): UseUniversalMediaUploaderReturn => {
  const { getOrCreateActor, removeActor, actors } = useUniversalMediaUploaderContext();
  const configRef = useRef(config);
  // Tracks file IDs whose next file-removed Uppy event should not be forwarded
  // to the machine as FILE_REMOVED. Used by auto-removal to keep status cards
  // visible while removing files from Uppy's internal queue.
  const suppressFileRemovedRef = useRef<Set<string>>(new Set());

  const suppressFileRemoved = useCallback((fileId: string) => {
    suppressFileRemovedRef.current.add(fileId);
  }, []);

  const unsuppressFileRemoved = useCallback((fileId: string) => {
    suppressFileRemovedRef.current.delete(fileId);
  }, []);

  // normalize uploaderId by trimming
  const normalizedUploaderId = uploaderId.trim();

  // Update config ref when it changes
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  const actor = useMemo(() => {
    const actorInstance = getOrCreateActor(normalizedUploaderId);

    if (configRef.current && actorInstance.getSnapshot().matches('uninitialized')) {
      actorInstance.send({
        type: EVENT_TYPES.INITIALIZE,
        restrictions: configRef.current.restrictions,
        onUploadParams: configRef.current.onUploadParams,
        onFinalize: configRef.current.onFinalize,
        onBackboneSync: configRef.current.onBackboneSync,
        persist: configRef.current.persist,
        dragDropTarget: configRef.current.dragDropTarget,
        currentTotalSize: configRef.current.currentTotalSize,
        currentFileCount: configRef.current.currentFileCount,
        multiFile: configRef.current.multiFile,
        downscaleTo: configRef.current.downscaleTo,
        allowDuplicates: configRef.current.allowDuplicates,
      });
    }

    return actorInstance;
  }, [normalizedUploaderId, getOrCreateActor]);

  const eventSenders = useMemo(() => ({
    fileAdded: (file: UppyFile<UppyMeta, UppyBody>) => {
      actor.send({ type: EVENT_TYPES.FILE_ADDED, file });
    },
    restrictionFailed: (file: UppyFile<UppyMeta, UppyBody> | undefined, error: Error) => {
      actor.send({ type: EVENT_TYPES.RESTRICTION_FAILED, file, error });
    },
    uploadProgress: (file: UppyFile<UppyMeta, UppyBody>, progress: { bytesUploaded: number; bytesTotal: number }) => {
      actor.send({ type: EVENT_TYPES.UPLOAD_PROGRESS, file, progress });
    },
    uploadSuccess: (file: UppyFile<UppyMeta, UppyBody>,
      response: { body?: UppyBody; status: number; bytesUploaded?: number; uploadURL?: string },
      attachmentId?: string) => {
      actor.send({
        type: EVENT_TYPES.UPLOAD_SUCCESS, file, response, attachmentId,
      });
    },
    uploadComplete: (result: {
      successful?: UppyFile<UppyMeta, UppyBody>[]; failed?: UppyFile<UppyMeta, UppyBody>[] }) => {
      actor.send({ type: EVENT_TYPES.UPLOAD_COMPLETE, result });
    },
    uploadError: (file: UppyFile<UppyMeta, UppyBody> | undefined, error: Error) => {
      actor.send({ type: EVENT_TYPES.UPLOAD_ERROR, file, error });
    },
    fileRemoved: (file: UppyFile<UppyMeta, UppyBody> | undefined) => {
      actor.send({ type: EVENT_TYPES.FILE_REMOVED, file });
    },
    cancelAll: () => {
      actor.send({ type: EVENT_TYPES.CANCEL_ALL });
    },
    removeFile: () => {
      actor.send({ type: EVENT_TYPES.REMOVE_FILE });
    },
    clearError: () => {
      actor.send({ type: EVENT_TYPES.CLEAR_ERROR });
    },
    reset: () => {
      actor.send({ type: EVENT_TYPES.RESET });
    },
    fileSizeUpdated: (file: UppyFile<UppyMeta, UppyBody>, size: number) => {
      actor.send({ type: EVENT_TYPES.FILE_SIZE_UPDATED, file, size });
    },
  }), [actor]);

  // Cleanup on unmount unless persist flag is set
  useEffect(() => () => {
    const actorInRegistry = actors.get(normalizedUploaderId);
    if (!actorInRegistry) {
      return;
    }

    const snapshot = actor.getSnapshot();
    const shouldPersist = snapshot.context.persist;

    if (!shouldPersist) {
      removeActor(normalizedUploaderId);
    }
  }, [normalizedUploaderId, actor, removeActor]);

  // Connect Uppy events to the state machine
  useEffect(() => {
    const snapshot = actor.getSnapshot();
    const uppy = snapshot.context.uppyInstance;

    if (!uppy) return undefined;

    const handleFileAdded = async (file: UppyFile<UppyMeta, UppyBody>) => {
      const currentSnapshot = actor.getSnapshot();
      eventSenders.fileAdded(file);

      const { onUploadParams } = currentSnapshot.context;

      // Helper: Check if all files are done and trigger UPLOAD_COMPLETE (for custom XHR uploads)
      const checkAndTriggerCompletion = () => {
        const completionSnapshot = actor.getSnapshot();
        if (!completionSnapshot.context.multiFileMode) return;

        const allFiles = uppy.getFiles();
        const { files } = completionSnapshot.context;

        const successful: typeof allFiles = [];
        const failed: typeof allFiles = [];

        for (const f of allFiles) {
          const entry = files.get(f.id);
          if (!entry || (!entry.attachmentData && !entry.error)) {
            return; // Not all done - exit early
          }
          if (entry.attachmentData) {
            successful.push(f);
          } else {
            failed.push(f);
          }
        }

        eventSenders.uploadComplete({ successful, failed });
      };

      // If onUploadParams is provided, handle custom upload
      if (onUploadParams) {
        try {
          // Resize oversized images before upload when a resolution limit is configured.
          // Falls back to the original file on any Canvas API failure.
          const { downscaleTo } = currentSnapshot.context;
          const fileData = typeof downscaleTo === 'number'
            ? await resizeImageIfNeeded(file.data as Blob, downscaleTo)
            : file.data as Blob;

          // If the file was resized, update Uppy's internal state so the machine and UI
          // reflect the actual upload size rather than the original file size.
          let effectiveFile = file;
          if (fileData !== file.data) {
            uppy.setFileState(file.id, { data: fileData, size: fileData.size });
            effectiveFile = uppy.getFile(file.id);

            // Notify the machine of the new size only when resize occurred.
            // The original size is already known from FILE_ADDED.
            eventSenders.fileSizeUpdated(effectiveFile, effectiveFile.size ?? fileData.size);
          }

          const fileMetadata = {
            meta: { name: effectiveFile.name || 'unnamed-file', size: fileData.size },
            file: fileData as File,
          };
          const uploadParams = await onUploadParams(fileMetadata);
          const { attachmentId } = uploadParams;

          // Create and configure XHR
          const xhr = new XMLHttpRequest();

          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              eventSenders.uploadProgress(effectiveFile, { bytesUploaded: e.loaded, bytesTotal: e.total });
            }
          });

          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              let parsedBody = {};
              const contentType = xhr.getResponseHeader('content-type') ?? '';

              if (xhr.responseText && contentType.includes('application/json')) {
                try {
                  parsedBody = JSON.parse(xhr.responseText);
                } catch (error) {
                  console.error('Failed to parse expected JSON response', {
                    url: uploadParams.url,
                    contentType,
                    status: xhr.status,
                    responsePreview: xhr.responseText.slice(0, 200),
                    error,
                  });
                }
              }

              const response = {
                body: parsedBody,
                status: xhr.status,
                bytesUploaded: fileData.size,
                uploadURL: uploadParams.url,
              };
              eventSenders.uploadSuccess(effectiveFile, response, attachmentId);
              checkAndTriggerCompletion();
            } else {
              const error = new Error(`Upload failed with status ${xhr.status}`);
              eventSenders.uploadError(effectiveFile, error);
              checkAndTriggerCompletion();
            }
          });

          xhr.addEventListener('error', () => {
            const error = new Error('Upload failed');
            eventSenders.uploadError(effectiveFile, error);
            checkAndTriggerCompletion();
          });

          // Execute upload
          xhr.open(uploadParams.method, uploadParams.url);
          Object.entries(uploadParams.headers).forEach(([key, value]) => {
            xhr.setRequestHeader(key, value);
          });
          xhr.send(uploadParams.body);
        } catch (error) {
          // Errors from onUploadParams (e.g., server rejecting due to limits) should be
          // treated as restriction failures so they're handled consistently with Uppy's
          // built-in validation. This ensures proper error formatting and state transitions.
          const restrictionError = error instanceof Error ? error : new Error(String(error));
          handleRestrictionFailed(file, restrictionError);
        }
      } else {
        // Use standard Uppy upload
        uppy.upload().catch((error) => {
          console.error('Uppy upload error:', error);
          const uploadError = error instanceof Error ? error : new Error('Network error.');
          eventSenders.uploadError(file, uploadError);
        });
      }
    };

    const handleRestrictionFailed = (file: UppyFile<UppyMeta, UppyBody> | undefined, error: any) => {
      const errorObj = error instanceof Error ? error : new Error(String(error));

      const { restrictions } = actor.getSnapshot().context;
      const formattedMessage = formatRestrictionError(errorObj, restrictions);
      const formattedError = new Error(formattedMessage);

      eventSenders.restrictionFailed(file, formattedError);
    };

    const handleUploadProgress = (
      file: UppyFile<UppyMeta, UppyBody> | undefined,
      progress: { bytesUploaded: number; bytesTotal: number | null },
    ) => {
      if (file && progress.bytesTotal !== null) {
        eventSenders.uploadProgress(file, {
          bytesUploaded: progress.bytesUploaded,
          bytesTotal: progress.bytesTotal,
        });
      }
    };

    const handleUploadSuccess = (
      file: UppyFile<UppyMeta, UppyBody> | undefined,
      response: { body?: UppyBody; status: number; bytesUploaded?: number; uploadURL?: string },
    ) => {
      if (file) {
        eventSenders.uploadSuccess(file, response);
      }
    };

    const handleUploadError = (file: UppyFile<UppyMeta, UppyBody> | undefined, error: any) => {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      eventSenders.uploadError(file, errorObj);
    };

    const handleFileRemoved = (file: UppyFile<UppyMeta, UppyBody> | undefined) => {
      if (file?.id && suppressFileRemovedRef.current.has(file.id)) {
        suppressFileRemovedRef.current.delete(file.id);
        return;
      }
      eventSenders.fileRemoved(file);
    };

    const handleComplete = (result: any) => {
      eventSenders.uploadComplete({
        successful: result.successful || [],
        failed: result.failed || [],
      });
    };

    const handleCancelAll = () => {
      eventSenders.cancelAll();
    };

    uppy.on('file-added', handleFileAdded);
    uppy.on('restriction-failed', handleRestrictionFailed);
    uppy.on('upload-progress', handleUploadProgress);
    uppy.on('upload-success', handleUploadSuccess);
    uppy.on('upload-error', handleUploadError);
    uppy.on('file-removed', handleFileRemoved);
    uppy.on('complete', handleComplete);
    uppy.on('cancel-all', handleCancelAll);

    return () => {
      uppy.off('file-added', handleFileAdded);
      uppy.off('restriction-failed', handleRestrictionFailed);
      uppy.off('upload-progress', handleUploadProgress);
      uppy.off('upload-success', handleUploadSuccess);
      uppy.off('upload-error', handleUploadError);
      uppy.off('file-removed', handleFileRemoved);
      uppy.off('complete', handleComplete);
      uppy.off('cancel-all', handleCancelAll);
    };
  }, [actor, normalizedUploaderId, eventSenders]);

  // State selectors
  const state = useSelector(actor, (snapshot) => {
    const stateValue = snapshot.value;
    if (typeof stateValue === 'string') {
      return stateValue;
    }
    // Handle nested states (e.g., { uploading: 'inProgress' })
    if (typeof stateValue === 'object' && stateValue !== null) {
      const parentState = Object.keys(stateValue)[0];
      const childState = stateValue[parentState as keyof typeof stateValue];
      if (typeof childState === 'string') {
        return `${parentState}.${childState}`;
      }
      return parentState;
    }
    return 'unknown';
  });

  const isUninitialized = useSelector(actor, (snapshot) => snapshot.matches('uninitialized'));
  const isIdle = useSelector(actor, (snapshot) => snapshot.matches('idle'));
  const isUploading = useSelector(actor, (snapshot) => snapshot.matches('validating') || snapshot.matches('uploading'));
  const isCompleted = useSelector(actor, (snapshot) => snapshot.matches('completed'));
  const isError = useSelector(actor, (snapshot) => snapshot.matches('error'));

  // Context selectors
  const currentFile = useSelector(actor, (snapshot) => {
    const { files, multiFileMode } = snapshot.context;

    // In single-file mode, return the first (and only) file from the map
    if (!multiFileMode && files.size > 0) {
      const firstEntry = Array.from(files.values())[0];
      return firstEntry?.file || null;
    }

    // In multi-file mode, return null (consumers should use files map)
    return null;
  });
  const uploadProgress = useSelector(actor, (snapshot) => snapshot.context.uploadProgress);
  const error = useSelector(actor, (snapshot) => snapshot.context.error);
  const attachmentData = useSelector(actor, (snapshot) => snapshot.context.attachmentData);
  const uppyInstance = useSelector(actor, (snapshot) => snapshot.context.uppyInstance);
  const wasRecentlyUploaded = useSelector(actor, (snapshot) => snapshot.context.wasRecentlyUploaded);
  const multiFileMode = useSelector(actor, (snapshot) => snapshot.context.multiFileMode);

  // Custom equality function for the files Map to prevent excessive re-renders.
  // Only triggers re-render when file count changes or file progress/status changes.
  const filesEqualityFn = useCallback((prevFiles: MachineContext['files'], nextFiles: MachineContext['files']) => {
    // Different number of files means state changed
    if (prevFiles.size !== nextFiles.size) {
      return false;
    }

    // Compare file IDs to detect additions/removals
    const prevFileIds = Array.from(prevFiles.keys());
    const nextFileIds = Array.from(nextFiles.keys());

    const fileIdsMatch = prevFileIds.every((id, index) => id === nextFileIds[index]);
    if (!fileIdsMatch) {
      return false;
    }

    // Compare meaningful values for each file entry
    for (const fileId of prevFileIds) {
      const prevEntry = prevFiles.get(fileId);
      const nextEntry = nextFiles.get(fileId);

      // Both entries must exist
      if (!prevEntry || !nextEntry) {
        return false;
      }

      // Check if upload progress changed
      const progressChanged = prevEntry.progress !== nextEntry.progress;
      if (progressChanged) {
        return false;
      }

      // Check if file size changed (e.g., after resizing via FILE_SIZE_UPDATED)
      // The UI displays file.size via filesInProgress, so this must trigger re-render
      const fileSizeChanged = prevEntry.file.size !== nextEntry.file.size;
      if (fileSizeChanged) {
        return false;
      }

      // Check if error status changed (presence/absence, not content)
      const errorStatusChanged = Boolean(prevEntry.error) !== Boolean(nextEntry.error);
      if (errorStatusChanged) {
        return false;
      }

      // Check if completion status changed (presence/absence of attachment data)
      const completionStatusChanged = Boolean(prevEntry.attachmentData) !== Boolean(nextEntry.attachmentData);
      if (completionStatusChanged) {
        return false;
      }
    }

    return true;
  }, []);

  const files = useSelector(actor, (snapshot) => snapshot.context.files, filesEqualityFn);
  const failedFiles = useSelector(actor, (snapshot) => snapshot.context.failedFiles);

  // Actions
  const initialize = useCallback(
    (initConfig: InitializeConfig) => {
      actor.send({
        type: EVENT_TYPES.INITIALIZE,
        restrictions: initConfig.restrictions,
        onUploadParams: initConfig.onUploadParams,
        onFinalize: initConfig.onFinalize,
        onBackboneSync: initConfig.onBackboneSync,
        persist: initConfig.persist,
        dragDropTarget: initConfig.dragDropTarget,
        currentTotalSize: initConfig.currentTotalSize,
        currentFileCount: initConfig.currentFileCount,
        multiFile: initConfig.multiFile,
        downscaleTo: initConfig.downscaleTo,
        allowDuplicates: initConfig.allowDuplicates,
      });
    },
    [actor],
  );

  const sendEvent = useCallback(
    (event: UniversalMediaUploaderEvent) => {
      actor.send(event);
    },
    [actor],
  );

  const cancelUpload = useCallback(() => {
    const snapshot = actor.getSnapshot();
    const uppy = snapshot.context.uppyInstance;
    if (uppy) {
      uppy.cancelAll();
    }
  }, [actor]);

  const removeFile = useCallback(() => {
    eventSenders.removeFile();
  }, [eventSenders]);

  const clearError = useCallback(() => {
    eventSenders.clearError();
  }, [eventSenders]);

  const reset = useCallback(() => {
    eventSenders.reset();
  }, [eventSenders]);

  return {
    // State
    state,
    isUninitialized,
    isIdle,
    isUploading,
    isCompleted,
    isError,

    // Context
    currentFile,
    uploadProgress,
    error,
    attachmentData,
    uppyInstance,
    wasRecentlyUploaded,
    multiFileMode,
    files,
    failedFiles,

    // Actions
    initialize,
    sendEvent,
    cancelUpload,
    removeFile,
    clearError,
    reset,
    suppressFileRemoved,
    unsuppressFileRemoved,
  };
};


